import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Messages } from 'primereact/messages';
import BootstrapButton from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { useNavigate } from 'react-router-dom';
import '../config.js';

const readJwtClaim = (token, claim) => {
	try {
		const parts = String(token || '').split('.');
		if (parts.length < 2) return null;
		const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
		const payload = JSON.parse(atob(padded));
		return payload && payload[claim] ? payload[claim] : null;
	} catch {
		return null;
	}
};

const getBestEmail = () => {
	const fromStorage = localStorage.getItem('userEmail');
	if (fromStorage) return fromStorage;

	const token = localStorage.getItem('token');
	if (!token) return '';

	return (
		readJwtClaim(token, 'email') ||
		readJwtClaim(token, 'upn') ||
		readJwtClaim(token, 'unique_name') ||
		readJwtClaim(token, 'preferred_username') ||
		''
	);
};

const extractAspNetModelStateErrors = (data) => {
	if (!data || typeof data !== 'object') return [];

	// Common ASP.NET Core shapes:
	// - { "OldPassword": ["Incorrect password."] }
	// - { title, status, errors: { OldPassword: ["..."] } }
	const candidate = data.errors && typeof data.errors === 'object' ? data.errors : data;
	if (!candidate || typeof candidate !== 'object') return [];

	const messages = [];
	for (const [key, value] of Object.entries(candidate)) {
		if (Array.isArray(value)) {
			for (const msg of value) {
				if (typeof msg === 'string' && msg.trim()) messages.push(msg.trim());
			}
		} else if (typeof value === 'string' && value.trim()) {
			// Less common, but handle it.
			messages.push(value.trim());
		}
	}

	// De-dupe while preserving order.
	return Array.from(new Set(messages));
};

const SupportAccount = () => {
	const navigate = useNavigate();
	const msgs = useRef(null);

	const email = useMemo(() => getBestEmail(), []);

	const [oldPassword, setOldPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [saving, setSaving] = useState(false);

	const submit = async (e) => {
		e.preventDefault();
		if (msgs.current) msgs.current.clear();

		if (!oldPassword || !newPassword) {
			if (msgs.current) {
				msgs.current.show({ severity: 'error', summary: '', detail: 'Please enter old and new password.' });
			}
			return;
		}

		try {
			setSaving(true);
			const token = localStorage.getItem('token');
			if (!token) {
				if (msgs.current) {
					msgs.current.show({
						severity: 'error',
						summary: '',
						detail: 'You are not logged in (missing token). Please log in again.',
					});
				}
				return;
			}
			await axios.post(
				global.config.url + '/auth/change-password',
				{
					oldPassword,
					newPassword,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			setOldPassword('');
			setNewPassword('');
			setShowOldPassword(false);
			setShowNewPassword(false);
			if (msgs.current) msgs.current.show({ severity: 'success', summary: '', detail: 'Password updated successfully.' });
		} catch (error) {
			console.error('Change password error:', error);
			const status = error && error.response ? error.response.status : undefined;
			const data = error && error.response ? error.response.data : undefined;

			if (status === 401) {
				if (msgs.current) {
					msgs.current.show({
						severity: 'error',
						summary: '',
						detail: 'Unauthorized (token missing/invalid/expired). Please log in again.',
					});
				}
				return;
			}

			if (status === 400) {
				const modelErrors = extractAspNetModelStateErrors(data);
				if (modelErrors.length > 0) {
					if (msgs.current) {
						modelErrors.forEach((msg) =>
							msgs.current.show({ severity: 'error', summary: '', detail: msg })
						);
					}
					return;
				}
			}

			let detail = 'Failed to update password.';
			if (data) {
				if (typeof data === 'string') detail = data;
				else if (data.message) detail = data.message;
				else if (data.title && typeof data.title === 'string') detail = data.title;
			}
			if (msgs.current) msgs.current.show({ severity: 'error', summary: '', detail });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card className="justify-content-md-center mt-5">
			<Container fluid="md">
				<div className="panel">
					<div className="flex align-content-center flex-wrap">
						<Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="flex mb-3" />
						<h4 className="flex mt-2 mb-3">Account</h4>
					</div>
				</div>
				<Form onSubmit={submit} autoComplete="off">
					<Form.Group className="mb-3" controlId="supportAccountEmail">
						<Form.Label>Email account</Form.Label>
						<Form.Control type="email" value={email} disabled />
					</Form.Group>

					<Form.Group className="mb-3" controlId="supportAccountOldPassword">
						<Form.Label>Old password</Form.Label>
						<InputGroup>
							<Form.Control
								type={showOldPassword ? 'text' : 'password'}
								autoComplete="off"
								value={oldPassword}
								onChange={(e) => setOldPassword(e.target.value)}
								required
							/>
							<BootstrapButton
								variant="outline-secondary"
								type="button"
								onClick={() => setShowOldPassword((v) => !v)}
								aria-label={showOldPassword ? 'Hide old password' : 'Show old password'}
								aria-pressed={showOldPassword}
							>
								{showOldPassword ? 'Hide' : 'Show'}
							</BootstrapButton>
						</InputGroup>
					</Form.Group>

					<Form.Group className="mb-3" controlId="supportAccountNewPassword">
						<Form.Label>New password</Form.Label>
						<InputGroup>
							<Form.Control
								type={showNewPassword ? 'text' : 'password'}
								autoComplete="new-password"
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								required
							/>
							<BootstrapButton
								variant="outline-secondary"
								type="button"
								onClick={() => setShowNewPassword((v) => !v)}
								aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
								aria-pressed={showNewPassword}
							>
								{showNewPassword ? 'Hide' : 'Show'}
							</BootstrapButton>
						</InputGroup>
					</Form.Group>

					<Messages ref={msgs} />
					<Button type="submit" label="Change password" disabled={saving} loading={saving} />
				</Form>
			</Container>
		</Card>
	);
};

export default SupportAccount;
